// src/services/unitsService.js
import ApiService from './api'

// 🏷️ Unit Types Constants
export const unitTypes = {
  QUANTITY: 'quantity',
  WEIGHT: 'weight', 
  VOLUME: 'volume'
}

// 🎨 Display Labels for Types
export const typeDisplayLabels = {
  quantity: 'Quantity',
  weight: 'Weight',
  volume: 'Volume'
}

// 📏 Unit Abbreviations by Type
export const unitAbbreviations = {
  [unitTypes.QUANTITY]: [
    { value: 'pcs', label: 'Pieces (pcs)' },
    { value: 'dozen', label: 'Dozen (dozen)' },
    { value: 'pack', label: 'Pack (pack)' }
  ],
  [unitTypes.WEIGHT]: [
    { value: 'mg', label: 'Milligram (mg)' },
    { value: 'g', label: 'Gram (g)' },
    { value: 'kg', label: 'Kilogram (kg)' }
  ],
  [unitTypes.VOLUME]: [
    { value: 'ml', label: 'Milliliter (ml)' },
    { value: 'l', label: 'Liter (l)' },
    { value: 'cup', label: 'Cup (cup)' }
  ]
}

// 📊 Type Options for Dropdown
export const typeOptions = [
  { value: unitTypes.QUANTITY, label: typeDisplayLabels[unitTypes.QUANTITY] },
  { value: unitTypes.WEIGHT, label: typeDisplayLabels[unitTypes.WEIGHT] },
  { value: unitTypes.VOLUME, label: typeDisplayLabels[unitTypes.VOLUME] }
]

class UnitsService {
  // 📏 Get all units
  async getAllUnits(params = {}) {
    try {
      console.log('📏 Fetching all units with params:', params)
      const response = await ApiService.get('/units', params)
      console.log('✅ Units fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching units:', error)
      throw error
    }
  }

  // 📏 Get unit by ID
  async getUnitById(id) {
    try {
      console.log('📏 Fetching unit by ID:', id)
      const endpoint = ApiService.replaceUrlParams('/units/:id', { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Unit fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching unit:', error)
      throw error
    }
  }

  // 📏 Create new unit
  async createUnit(unitData) {
    try {
      console.log('📏 Creating new unit with data:', unitData)
      
      // Validate unit data before sending
      this.validateUnitData(unitData)
      
      const response = await ApiService.post('/units', unitData)
      console.log('✅ Unit created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating unit:', error)
      throw error
    }
  }

  // 📏 Update unit using PATCH
  async updateUnit(id, unitData) {
    try {
      console.log('📏 Updating unit:', { id, unitData })
      
      // For updates, we don't validate all fields since some may be read-only
      // Only validate the fields that are being updated
      const updateErrors = this.validatePartialUnitData(unitData)
      if (updateErrors.length > 0) {
        throw new Error(updateErrors.join(', '))
      }
      
      const endpoint = ApiService.replaceUrlParams('/units/:id', { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, unitData)
      console.log('✅ Unit updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating unit:', error)
      throw error
    }
  }

  // 📏 Delete unit
  async deleteUnit(id) {
    try {
      console.log('📏 Deleting unit with ID:', id)
      const endpoint = ApiService.replaceUrlParams('/units/:id', { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Unit deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting unit:', error)
      throw error
    }
  }

  // 📏 Toggle unit status
  async toggleUnitStatus(id, isActive) {
    try {
      console.log('📏 Toggling unit status:', { id, isActive })
      const endpoint = ApiService.replaceUrlParams('/units/:id/status', { id })
      const response = await ApiService.patch(endpoint, { isActive })
      console.log('✅ Unit status toggled successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error toggling unit status:', error)
      throw error
    }
  }

  // 📏 Bulk operations
  async bulkDeleteUnits(unitIds) {
    try {
      console.log('📏 Bulk deleting units:', unitIds)
      const response = await ApiService.post('/units/bulk-delete', {
        ids: unitIds
      })
      console.log('✅ Units bulk deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting units:', error)
      throw error
    }
  }

  // 📏 Bulk status update
  async bulkUpdateStatus(unitIds, isActive) {
    try {
      console.log('📏 Bulk updating unit status:', { unitIds, isActive })
      const response = await ApiService.patch('/units/bulk-status', {
        ids: unitIds,
        isActive
      })
      console.log('✅ Units bulk status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk updating unit status:', error)
      throw error
    }
  }

  // 📏 Get unit statistics
  async getUnitStats() {
    try {
      console.log('📏 Fetching unit statistics')
      const response = await ApiService.get('/units/stats')
      console.log('✅ Unit statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching unit statistics:', error)
      throw error
    }
  }

  // 📏 Validate unit name (check for duplicates)
  async validateUnitName(name, excludeId = null) {
    try {
      console.log('📏 Validating unit name:', { name, excludeId })
      const params = {
        name,
        ...(excludeId && { excludeId })
      }
      const response = await ApiService.get('/units/validate-name', params)
      console.log('✅ Unit name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating unit name:', error)
      throw error
    }
  }

  // 🔍 Helper Methods for Frontend
  
  // Get abbreviations for specific type
  getAbbreviationsForType(type) {
    return unitAbbreviations[type] || []
  }

  // Get all available types
  getAllTypes() {
    return typeOptions
  }

  // Check if abbreviation is valid for type
  isValidAbbreviation(type, abbreviation) {
    const validAbbreviations = this.getAbbreviationsForType(type)
    return validAbbreviations.some(abbr => abbr.value === abbreviation)
  }

  // Get type by abbreviation (reverse lookup)
  getTypeByAbbreviation(abbreviation) {
    for (const [type, abbreviations] of Object.entries(unitAbbreviations)) {
      if (abbreviations.some(abbr => abbr.value === abbreviation)) {
        return type
      }
    }
    return null
  }

  // Format unit display name
  formatUnitDisplay(unitName, abbreviation, type) {
    return `${unitName} (${abbreviation}) - ${type}`
  }

  // 🛡️ Validation Methods
  validateUnitData(unitData) {
    const { name, abbreviation, type } = unitData

    // Check required fields
    if (!name || !name.trim()) {
      throw new Error('Unit name is required')
    }

    if (!abbreviation || !abbreviation.trim()) {
      throw new Error('Abbreviation is required')
    }

    if (!type || !type.trim()) {
      throw new Error('Type is required')
    }

    // Check if type is valid
    if (!Object.values(unitTypes).includes(type)) {
      throw new Error('Invalid unit type')
    }

    // Check if abbreviation is valid for the selected type
    if (!this.isValidAbbreviation(type, abbreviation)) {
      throw new Error(`Invalid abbreviation '${abbreviation}' for type '${type}'`)
    }

    return true
  }

  // 🛡️ Validate partial data for updates (only validate provided fields)
  validatePartialUnitData(unitData) {
    const errors = []

    // Only validate fields that are provided
    if (unitData.name !== undefined) {
      if (!unitData.name || !unitData.name.trim()) {
        errors.push('Unit name cannot be empty')
      }
    }

    if (unitData.type !== undefined) {
      if (!Object.values(unitTypes).includes(unitData.type)) {
        errors.push('Invalid unit type')
      }
    }

    if (unitData.abbreviation !== undefined && unitData.type !== undefined) {
      if (!this.isValidAbbreviation(unitData.type, unitData.abbreviation)) {
        errors.push(`Invalid abbreviation '${unitData.abbreviation}' for type '${unitData.type}'`)
      }
    }

    return errors
  }

  // 📝 Search and Filter Methods
  searchUnits(units, searchTerm) {
    if (!searchTerm) return units

    const term = searchTerm.toLowerCase()
    return units.filter(unit => 
      unit.name?.toLowerCase().includes(term) ||
      unit.abbreviation?.toLowerCase().includes(term) ||
      unit.type?.toLowerCase().includes(term) ||
      unit.description?.toLowerCase().includes(term)
    )
  }

  // Filter units by type
  filterByType(units, type) {
    if (!type) return units
    return units.filter(unit => unit.type === type)
  }

  // Filter units by status
  filterByStatus(units, isActive) {
    if (isActive === null || isActive === undefined) return units
    return units.filter(unit => unit.isActive === isActive)
  }

  // Sort units
  sortUnits(units, sortBy = 'name', sortOrder = 'asc') {
    return [...units].sort((a, b) => {
      let valueA = a[sortBy]
      let valueB = b[sortBy]

      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB.toLowerCase()
      }

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }

  // 📊 Analytics Methods
  getUnitsSummary(units) {
    const summary = {
      total: units.length,
      active: units.filter(unit => unit.isActive).length,
      inactive: units.filter(unit => !unit.isActive).length,
      byType: {}
    }

    // Count by type
    Object.values(unitTypes).forEach(type => {
      summary.byType[type] = units.filter(unit => unit.type === type).length
    })

    return summary
  }

  // Get most used units (if usage data is available)
  getMostUsedUnits(units, usageData = []) {
    return units
      .map(unit => ({
        ...unit,
        usageCount: usageData.filter(usage => usage.unitId === unit.id).length
      }))
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 5)
  }

  // 🎨 UI Helper Methods
  getStatusColor(isActive) {
    return isActive ? 'success' : 'error'
  }

  getStatusLabel(isActive) {
    return isActive ? 'Active' : 'Inactive'
  }

  getTypeIcon(type) {
    const icons = {
      [unitTypes.QUANTITY]: '📦',
      [unitTypes.WEIGHT]: '⚖️',
      [unitTypes.VOLUME]: '🥤'
    }
    return icons[type] || '📏'
  }

  getTypeColor(type) {
    const colors = {
      [unitTypes.QUANTITY]: 'blue',
      [unitTypes.WEIGHT]: 'green',
      [unitTypes.VOLUME]: 'purple'
    }
    return colors[type] || 'gray'
  }

  // Get display label for type
  getTypeDisplayLabel(type) {
    return typeDisplayLabels[type] || type
  }
}

export default new UnitsService()